/**
 * File validation utilities for secure file uploads
 * Includes magic byte verification, MIME type validation, and filename sanitization
 */

// File size limits
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
export const MIN_FILE_SIZE_BYTES = 100; // Minimum 100 bytes to avoid empty files

// Allowed MIME types
export const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/tiff',
]);

// Allowed extensions
export const ALLOWED_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png', 'webp', 'tiff', 'tif']);

/**
 * Magic byte signatures for file type verification
 * These are the first few bytes that identify the actual file type
 */
const MAGIC_BYTES = {
  // PDF: %PDF-
  pdf: {
    offset: 0,
    signature: [0x25, 0x50, 0x44, 0x46, 0x2D], // %PDF-
  },
  // JPEG: FFD8FF
  jpeg: {
    offset: 0,
    signature: [0xFF, 0xD8, 0xFF],
  },
  // PNG: 89504E47
  png: {
    offset: 0,
    signature: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],
  },
  // WebP: RIFF....WEBP
  webp: {
    offset: 0,
    signature: [0x52, 0x49, 0x46, 0x46], // RIFF (first 4 bytes)
    secondaryOffset: 8,
    secondarySignature: [0x57, 0x45, 0x42, 0x50], // WEBP (bytes 8-11)
  },
  // TIFF: Little-endian (49 49 2A 00) or Big-endian (4D 4D 00 2A)
  tiff_le: {
    offset: 0,
    signature: [0x49, 0x49, 0x2A, 0x00], // Little-endian
  },
  tiff_be: {
    offset: 0,
    signature: [0x4D, 0x4D, 0x00, 0x2A], // Big-endian
  },
};

/**
 * Read the first N bytes of a file
 * @param {File} file - File to read
 * @param {number} numBytes - Number of bytes to read
 * @returns {Promise<Uint8Array>} First bytes of the file
 */
async function readFileBytes(file, numBytes = 16) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const slice = file.slice(0, numBytes);
    reader.onload = () => {
      resolve(new Uint8Array(reader.result));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(slice);
  });
}

/**
 * Check if bytes match a signature at a given offset
 * @param {Uint8Array} bytes - File bytes
 * @param {number[]} signature - Expected signature bytes
 * @param {number} offset - Offset to start checking
 * @returns {boolean} True if signature matches
 */
function matchesSignature(bytes, signature, offset = 0) {
  if (bytes.length < offset + signature.length) return false;
  for (let i = 0; i < signature.length; i++) {
    if (bytes[offset + i] !== signature[i]) return false;
  }
  return true;
}

/**
 * Verify file type using magic bytes
 * @param {File} file - File to verify
 * @returns {Promise<{valid: boolean, detectedType: string|null, error?: string}>}
 */
export async function verifyFileMagicBytes(file) {
  try {
    const bytes = await readFileBytes(file, 16);

    // Check PDF
    if (matchesSignature(bytes, MAGIC_BYTES.pdf.signature, MAGIC_BYTES.pdf.offset)) {
      return { valid: true, detectedType: 'application/pdf' };
    }

    // Check JPEG
    if (matchesSignature(bytes, MAGIC_BYTES.jpeg.signature, MAGIC_BYTES.jpeg.offset)) {
      return { valid: true, detectedType: 'image/jpeg' };
    }

    // Check PNG
    if (matchesSignature(bytes, MAGIC_BYTES.png.signature, MAGIC_BYTES.png.offset)) {
      return { valid: true, detectedType: 'image/png' };
    }

    // Check WebP (RIFF + WEBP)
    if (matchesSignature(bytes, MAGIC_BYTES.webp.signature, MAGIC_BYTES.webp.offset)) {
      // Need to also check for WEBP at offset 8
      if (bytes.length >= 12 &&
          matchesSignature(bytes, MAGIC_BYTES.webp.secondarySignature, MAGIC_BYTES.webp.secondaryOffset)) {
        return { valid: true, detectedType: 'image/webp' };
      }
    }

    // Check TIFF (little-endian or big-endian)
    if (matchesSignature(bytes, MAGIC_BYTES.tiff_le.signature, MAGIC_BYTES.tiff_le.offset) ||
        matchesSignature(bytes, MAGIC_BYTES.tiff_be.signature, MAGIC_BYTES.tiff_be.offset)) {
      return { valid: true, detectedType: 'image/tiff' };
    }

    return {
      valid: false,
      detectedType: null,
      error: 'File content does not match any allowed type. The file may be corrupted or disguised.'
    };
  } catch (error) {
    return {
      valid: false,
      detectedType: null,
      error: 'Unable to read file content for verification.'
    };
  }
}

/**
 * Sanitize filename to prevent path traversal and other attacks
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename
 */
export function sanitizeFilename(filename) {
  if (!filename || typeof filename !== 'string') {
    return `file_${Date.now()}`;
  }

  // Remove path separators to prevent directory traversal
  let sanitized = filename
    .replace(/\.\./g, '') // Remove parent directory references
    .replace(/[\/\\]/g, '') // Remove path separators
    .replace(/^\.+/, ''); // Remove leading dots

  // Remove null bytes and other control characters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');

  // Remove or replace potentially dangerous characters
  sanitized = sanitized
    .replace(/[<>:"|?*]/g, '_') // Replace Windows reserved characters
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Collapse multiple underscores
    .trim();

  // Ensure the filename isn't empty after sanitization
  if (!sanitized || sanitized === '_') {
    sanitized = `file_${Date.now()}`;
  }

  // Limit filename length (max 255 characters is common filesystem limit)
  if (sanitized.length > 200) {
    const ext = sanitized.split('.').pop();
    const nameWithoutExt = sanitized.slice(0, -ext.length - 1);
    sanitized = nameWithoutExt.slice(0, 190) + '.' + ext;
  }

  return sanitized;
}

/**
 * Get file extension from filename
 * @param {string} filename - Filename to extract extension from
 * @returns {string|null} Lowercase extension or null
 */
export function getFileExtension(filename) {
  if (!filename || typeof filename !== 'string') return null;
  const parts = filename.split('.');
  if (parts.length < 2) return null;
  return parts.pop().toLowerCase();
}

/**
 * Comprehensive file validation
 * @param {File} file - File to validate
 * @returns {Promise<{valid: boolean, errors: string[], sanitizedName: string}>}
 */
export async function validateFile(file) {
  const errors = [];

  // Check if file exists
  if (!file) {
    return { valid: false, errors: ['No file provided'], sanitizedName: '' };
  }

  // Check file size
  const size = typeof file.size === 'number' ? file.size : 0;
  if (size < MIN_FILE_SIZE_BYTES) {
    errors.push('File appears to be empty or too small.');
  }
  if (size > MAX_FILE_SIZE_BYTES) {
    errors.push(`File exceeds the ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB limit.`);
  }

  // Sanitize filename
  const sanitizedName = sanitizeFilename(file.name);

  // Check extension
  const extension = getFileExtension(file.name);
  if (!extension || !ALLOWED_EXTENSIONS.has(extension)) {
    errors.push('File extension not allowed. Supported formats: PDF, JPG, PNG, WebP, TIFF.');
  }

  // Check MIME type (if browser provides it)
  const mimeType = file.type;
  if (mimeType && !ALLOWED_MIME_TYPES.has(mimeType)) {
    errors.push(`File type "${mimeType}" is not allowed.`);
  }

  // Verify magic bytes (most important check)
  const magicBytesResult = await verifyFileMagicBytes(file);
  if (!magicBytesResult.valid) {
    errors.push(magicBytesResult.error || 'File content verification failed.');
  } else {
    // If magic bytes detected a different type than declared, warn
    if (mimeType && magicBytesResult.detectedType !== mimeType) {
      // Allow JPEG variations
      const isJpegVariation =
        (mimeType === 'image/jpeg' && magicBytesResult.detectedType === 'image/jpeg') ||
        (mimeType === 'image/jpg' && magicBytesResult.detectedType === 'image/jpeg');

      if (!isJpegVariation) {
        errors.push(
          `File content (${magicBytesResult.detectedType}) doesn't match declared type (${mimeType}).`
        );
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    sanitizedName,
    detectedType: magicBytesResult.detectedType,
  };
}

/**
 * Quick validation without magic byte verification (for performance)
 * Use full validateFile() for uploads
 * @param {File} file - File to validate
 * @returns {{valid: boolean, error?: string}}
 */
export function quickValidateFile(file) {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  const size = typeof file.size === 'number' ? file.size : 0;
  if (size <= 0) {
    return { valid: false, error: 'File appears to be empty' };
  }
  if (size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, error: 'File exceeds size limit' };
  }

  const extension = getFileExtension(file.name);
  if (!extension || !ALLOWED_EXTENSIONS.has(extension)) {
    return { valid: false, error: 'Unsupported file type' };
  }

  return { valid: true };
}

export default {
  validateFile,
  quickValidateFile,
  verifyFileMagicBytes,
  sanitizeFilename,
  getFileExtension,
  MAX_FILE_SIZE_BYTES,
  MIN_FILE_SIZE_BYTES,
  ALLOWED_MIME_TYPES,
  ALLOWED_EXTENSIONS,
};
